import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { DraggableComponentProps } from 'features/document/constants';
import { isApprovalTableElement } from 'utils/typeGuards';
import '../Table/Table.css';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { updateContentElementById, updateElementById } from 'features/document/documentSlice';
import { TableCell } from '../Table/Table.utils';
import { AlignmentType, getApprovalInfoKeyByCellIdentifier } from './ApprovalTable.utils';
import { selectContract } from 'features/salesforce/salesforceSlice';

export default function ApprovalTable({ currentElement }: DraggableComponentProps) {
  if (!isApprovalTableElement(currentElement)) {
    return null;
  }

  const dispatch = useAppDispatch();
  const contract = useAppSelector(selectContract);

  const tableContent = currentElement.content;
  const {
    table: { columns, rows, cells },
    approvalInfo,
  } = tableContent;

  useEffect(() => {
    const { AccountName, OwnerName, CustomerSignedBy, CustomerSignedTitle, CompanySignedBy } = contract;

    const newContent = {
      ...tableContent,
      table: {
        ...tableContent.table,
        cells: {
          ...cells,
          'cell-2': { id: 'cell-2', content: AccountName || '[Account.Name]' },
          'cell-4': { id: 'cell-4', content: OwnerName || '[Company.Name]', rowSpan: 2 },
          'cell-6': { id: 'cell-6', content: CustomerSignedBy || '[Customer.Signed.By]', backgroundColor: '#f6f8fb' },
          'cell-8': { id: 'cell-8', content: CustomerSignedTitle || '[Customer.Signed.Title]' },
          'cell-10': { id: 'cell-10', content: CompanySignedBy || '[Company.Signed.By]', backgroundColor: '#f6f8fb' },
        },
      },
    };

    dispatch(
      updateElementById({
        elementId: currentElement.id,
        content: newContent,
        style: {},
      })
    );
  }, [dispatch, contract]);

  const handleOnClickCell = (activeSidebar: string) => {
    dispatch(
      updateContentElementById({
        content: {
          ...currentElement.content,
          activeSidebar: activeSidebar,
        },
      })
    );
  };

  const getSidebarByCellId = (cellId: string) => {
    const identifier = cellId.split('cell-')[1];
    switch (identifier) {
      case 'SIGNATURE_CUSTOMER':
        return 'customerSignature';
      case 'DATE_CUSTOMER':
        return 'customerSignedDate';
      case 'DATE_COMPANY':
        return 'companySignedDate';
      case 'SIGNATURE_COMPANY':
        return 'companySignature';
      default:
        return 'approvalTable';
    }
  };

  const getBgColorClass = (cell: TableCell) => {
    let className = '';
    if (cell.backgroundColor) {
      className = 'approvalEvenCell';
    }
    if (cell.id.includes('SIGNATURE') || cell.id.includes('DATE')) {
      className = className + ' ' + 'approvalClickableCell';
    }
    return className;
  };

  const getCellJustifyContentByCellId = (cellId: string) => {
    const identifier = cellId.split('cell-')[1];
    const key = getApprovalInfoKeyByCellIdentifier(identifier);

    if (key) {
      return approvalInfo[key].justifyContent as AlignmentType;
    }

    return 'left';
  };

  const renderCell = (cellContent: string) => {
    return <span>{cellContent}</span>;
  };

  const renderTableHeader = () => {
    return (
      <thead>
        <tr>
          {columns.map(column => (
            <th
              className='approvalHeader'
              key={column.id}
              style={{ display: column.hidden ? 'none' : 'table-cell', textAlign: 'left' }}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
    );
  };

  const renderTableBody = () => {
    return (
      <tbody>
        {rows.map(row => (
          <tr className={'row approvalRow'} key={row.id}>
            {row.cellIds.map((cellId, cellIndex) => {
              const cell = cells[cellId];
              return (
                <td
                  className={`approvalCell ${(cellIndex + 1) % 2 ? 'approvalHeader' : ''} ${getBgColorClass(cell)}`}
                  colSpan={cell.colSpan}
                  key={cell.id}
                  rowSpan={cell.rowSpan}
                  style={{
                    display: cell.hidden ? 'none' : 'table-cell',
                    fontWeight: (cellIndex + 1) % 2 ? 500 : 400,
                    width: '25%',
                    fontSize: '14px',
                    textAlign: getCellJustifyContentByCellId(cell.id),
                  }}
                  onClick={() => {
                    handleOnClickCell(getSidebarByCellId(cell.id));
                  }}
                  aria-hidden="true"
                >
                  {renderCell(cell.content)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    );
  };

  return (
    <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'} gap={'32px'}>
      <table className='table'>
        {renderTableHeader()}
        {renderTableBody()}
      </table>
    </Box>
  );
}
